import { useRef, useEffect, useCallback } from 'react'

const INPUT_TOKENS = ['J', 'P', 'D', 'Z']
const LAYER_COUNTS = [4, 6, 8, 6, 8, 6, 8, 4]
const LAYER_LABELS = ['input', 'embed', 'attn', 'ffn', 'attn', 'ffn', 'attn', 'output']

interface Node {
  x: number
  y: number
  layer: number
  activation: number
}

interface Edge {
  from: number
  to: number
}

function buildNetwork(w: number, h: number) {
  const nodes: Node[] = []
  const edges: Edge[] = []

  const padX = w * 0.12
  const padY = h * 0.1
  const usableW = w - padX * 2
  const usableH = h - padY * 2

  LAYER_COUNTS.forEach((count, li) => {
    const x = padX + (li / (LAYER_COUNTS.length - 1)) * usableW
    for (let i = 0; i < count; i++) {
      const y = padY + ((i + 0.5) / count) * usableH
      nodes.push({ x, y, layer: li, activation: 0 })
    }
  })

  let offset = 0
  for (let li = 0; li < LAYER_COUNTS.length - 1; li++) {
    const curr = LAYER_COUNTS[li]
    const next = LAYER_COUNTS[li + 1]
    const nextOff = offset + curr

    for (let i = 0; i < curr; i++) {
      const ratio = i / Math.max(curr - 1, 1)
      const center = ratio * (next - 1)

      for (let j = 0; j < next; j++) {
        const d = Math.abs(j - center) / Math.max(next - 1, 1)
        if (d < 0.35) {
          edges.push({ from: offset + i, to: nextOff + j })
        }
      }
    }
    offset += curr
  }

  return { nodes, edges }
}

function lerp(a: number, b: number, t: number) {
  return a + (b - a) * Math.max(0, Math.min(1, t))
}

function lerpColor(r1: number, g1: number, b1: number, r2: number, g2: number, b2: number, t: number): [number, number, number] {
  t = Math.max(0, Math.min(1, t))
  return [lerp(r1, r2, t), lerp(g1, g2, t), lerp(b1, b2, t)]
}

function smoothstep(a: number, b: number, x: number) {
  const t = Math.max(0, Math.min(1, (x - a) / (b - a)))
  return t * t * (3 - 2 * t)
}

export function HeroScene() {
  const canvasRef = useRef<HTMLCanvasElement>(null)
  const networkRef = useRef<{ nodes: Node[]; edges: Edge[] } | null>(null)
  const animRef = useRef<number>(0)
  const timeRef = useRef(0)
  const lastFrameRef = useRef(0)

  const resize = useCallback(() => {
    const canvas = canvasRef.current
    if (!canvas) return
    const parent = canvas.parentElement
    if (!parent) return
    const dpr = Math.min(window.devicePixelRatio, 2)
    const w = parent.clientWidth
    const h = parent.clientHeight
    canvas.width = w * dpr
    canvas.height = h * dpr
    canvas.style.width = w + 'px'
    canvas.style.height = h + 'px'
    networkRef.current = buildNetwork(w * dpr, h * dpr)
  }, [])

  useEffect(() => {
    resize()
    window.addEventListener('resize', resize)
    return () => window.removeEventListener('resize', resize)
  }, [resize])

  useEffect(() => {
    const canvas = canvasRef.current
    if (!canvas) return
    const ctx = canvas.getContext('2d')
    if (!ctx) return

    lastFrameRef.current = performance.now()

    const draw = (now: number) => {
      const dt = (now - lastFrameRef.current) / 1000
      lastFrameRef.current = now
      timeRef.current += dt

      const net = networkRef.current
      if (!net) { animRef.current = requestAnimationFrame(draw); return }

      const { nodes, edges } = net
      const w = canvas.width
      const h = canvas.height
      const dpr = Math.min(window.devicePixelRatio, 2)

      ctx.clearRect(0, 0, w, h)

      // Forward pass wave
      const cycle = 5.5
      const phase = (timeRef.current % cycle) / cycle
      const totalLayers = LAYER_COUNTS.length

      // Soft ease-in: first 12% of cycle ramps up intensity
      const startEase = phase < 0.12 ? smoothstep(0, 0.12, phase) : 1.0
      const waveFront = Math.max(0, (phase - 0.06) * 1.6)

      let offset = 0
      for (let li = 0; li < totalLayers; li++) {
        const lp = li / (totalLayers - 1)
        const d = waveFront - lp

        let v = 0
        if (d > -0.15 && d < 0.5) {
          if (d < 0) v = 1 + d / 0.15
          else if (d < 0.25) v = 1.0
          else v = 1.0 - (d - 0.25) / 0.25
        }
        v *= startEase
        if (phase > 0.78) v *= 1 - (phase - 0.78) / 0.22
        v = Math.max(0, Math.min(1, v))
        v = v * v * (3 - 2 * v)

        for (let i = 0; i < LAYER_COUNTS[li]; i++) {
          const idx = offset + i
          const jitter = Math.sin(timeRef.current * 2 + idx * 1.7) * 0.03
          nodes[idx].activation = Math.max(0, Math.min(1, v + jitter * v))
        }
        offset += LAYER_COUNTS[li]
      }

      // Draw edges
      for (const edge of edges) {
        const from = nodes[edge.from]
        const to = nodes[edge.to]
        const s = Math.min(from.activation, to.activation)

        const alpha = lerp(0.06, 0.35, s)
        const [r, g, b] = lerpColor(200, 190, 180, 110, 31, 27, s)

        ctx.beginPath()
        ctx.moveTo(from.x, from.y)
        ctx.lineTo(to.x, to.y)
        ctx.strokeStyle = `rgba(${r|0},${g|0},${b|0},${alpha})`
        ctx.lineWidth = lerp(0.5, 1.2, s) * dpr
        ctx.stroke()
      }

      // Draw pulses on edges
      for (const edge of edges) {
        const from = nodes[edge.from]
        const to = nodes[edge.to]
        const act = from.activation

        if (act < 0.3) continue

        const pulsePhase = (timeRef.current * 0.8 + edge.from * 0.3 + edge.to * 0.17) % 1
        const px = lerp(from.x, to.x, pulsePhase)
        const py = lerp(from.y, to.y, pulsePhase)
        const glow = Math.sin(pulsePhase * Math.PI)
        const pulseR = lerp(2, 4, glow) * dpr * act

        if (pulseR > 0.5) {
          ctx.beginPath()
          ctx.arc(px, py, pulseR, 0, Math.PI * 2)
          ctx.fillStyle = `rgba(110,31,27,${0.5 * glow * act})`
          ctx.fill()
        }
      }

      // Draw nodes
      for (const node of nodes) {
        const v = node.activation
        const radius = lerp(4, 7, v) * dpr

        // Outer glow for active nodes
        if (v > 0.3) {
          const glowR = radius * 2.5
          const gradient = ctx.createRadialGradient(node.x, node.y, radius * 0.5, node.x, node.y, glowR)
          gradient.addColorStop(0, `rgba(110,31,27,${0.15 * v})`)
          gradient.addColorStop(1, 'rgba(110,31,27,0)')
          ctx.beginPath()
          ctx.arc(node.x, node.y, glowR, 0, Math.PI * 2)
          ctx.fillStyle = gradient
          ctx.fill()
        }

        // Node circle
        const [r, g, b] = v < 0.4
          ? lerpColor(210, 200, 190, 140, 50, 40, v / 0.4)
          : lerpColor(140, 50, 40, 74, 19, 16, (v - 0.4) / 0.6)

        ctx.beginPath()
        ctx.arc(node.x, node.y, radius, 0, Math.PI * 2)
        ctx.fillStyle = `rgb(${r|0},${g|0},${b|0})`
        ctx.fill()

        // Subtle inner highlight
        if (v > 0.2) {
          const hlGrad = ctx.createRadialGradient(
            node.x - radius * 0.25, node.y - radius * 0.25, 0,
            node.x, node.y, radius
          )
          hlGrad.addColorStop(0, `rgba(255,255,255,${0.12 * v})`)
          hlGrad.addColorStop(1, 'rgba(255,255,255,0)')
          ctx.beginPath()
          ctx.arc(node.x, node.y, radius, 0, Math.PI * 2)
          ctx.fillStyle = hlGrad
          ctx.fill()
        }
      }

      // Input token labels
      const inputNodes = nodes.filter(n => n.layer === 0)
      ctx.textAlign = 'right'
      ctx.textBaseline = 'middle'
      for (let i = 0; i < INPUT_TOKENS.length; i++) {
        const node = inputNodes[i]
        if (!node) continue
        const v = node.activation
        const [r, g, b] = lerpColor(180, 170, 160, 74, 19, 16, v)
        ctx.font = `600 ${12 * dpr}px 'IBM Plex Mono', monospace`
        ctx.fillStyle = `rgba(${r|0},${g|0},${b|0},${lerp(0.35, 1, v)})`
        ctx.fillText(INPUT_TOKENS[i], node.x - 14 * dpr, node.y)
      }

      // Output dots
      const outputNodes = nodes.filter(n => n.layer === totalLayers - 1)
      for (const node of outputNodes) {
        const v = node.activation
        const r2 = lerp(2, 4, v) * dpr
        const [r, g, b] = lerpColor(210, 200, 190, 110, 31, 27, v)
        ctx.beginPath()
        ctx.arc(node.x + 14 * dpr, node.y, r2, 0, Math.PI * 2)
        ctx.fillStyle = `rgba(${r|0},${g|0},${b|0},${lerp(0.2, 0.8, v)})`
        ctx.fill()
      }

      // Layer labels
      ctx.textAlign = 'center'
      ctx.textBaseline = 'top'
      ctx.font = `500 ${7 * dpr}px 'IBM Plex Mono', monospace`
      ctx.fillStyle = 'rgba(180,170,160,0.5)'
      offset = 0
      for (let li = 0; li < totalLayers; li++) {
        const firstNode = nodes[offset]
        if (firstNode) {
          ctx.fillText(LAYER_LABELS[li].toUpperCase(), firstNode.x, h - 16 * dpr)
        }
        offset += LAYER_COUNTS[li]
      }

      animRef.current = requestAnimationFrame(draw)
    }

    animRef.current = requestAnimationFrame(draw)
    return () => cancelAnimationFrame(animRef.current)
  }, [])

  return (
    <canvas
      ref={canvasRef}
      className="w-full h-full"
      style={{ background: 'transparent' }}
    />
  )
}
